import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';

import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';
import { getRemoteRuntime } from '@adsk/exo-skeleton-api';
import { useRemotes } from '@adsk/exo-skeleton-react';

import { useFeatureFlag } from '../components/LaunchDarklyProvider';
import OverrideOverlay from '../components/OverrideOverlay/OverrideOverlay';
import usePreview from '../hooks/usePreview';
import { getRemoteOverrides } from '../utils/getRemoteOverrides';
import { shouldShowReactScanInDevBanner } from '../utils/reactScan';
import safeParseObjectFromSessionStorage from '../utils/safeParseObjectFromSessionStorage';

/**
 * Context for remote overrides
 */
type OverridesContextType = {
  overrides: Overrides;
};

const RemoteOverridesContext = createContext<OverridesContextType>({
  overrides: {},
});

/**
 * Hook for accessing remote overrides
 */
export const useRemoteOverrides = () => useContext(RemoteOverridesContext);

export const OVERRIDE_SESSION_STORAGE_KEY = 'ACS_EXO_REMOTE_OVERRIDES';

/**
 * Type definition for overrides
 */
type Overrides = {
  [key: string]: string;
};

/**
 * Provider component for remote overrides
 */
const RemoteOverridesProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const { isLoading: isLoadingRemotesList } = useRemotes();
  const { value: enableRemoteOverridesFF } = useFeatureFlag('remote-overrides');
  const { preview, onPreviewResetClick } = usePreview();
  const currentEnv = useMemo(() => getEnv(), []);

  const isRemoteOverridesEnabled = useMemo(
    () =>
      !isLoadingRemotesList &&
      (currentEnv === ENVS.QA || enableRemoteOverridesFF),
    [isLoadingRemotesList, currentEnv, enableRemoteOverridesFF],
  );

  /**
   * Handles resetting a remote override
   */
  const onRemoteOverrideResetClick = (scope: string) => {
    const overridesFromSessionStorage = safeParseObjectFromSessionStorage(
      OVERRIDE_SESSION_STORAGE_KEY,
    );

    delete overridesFromSessionStorage[scope];

    sessionStorage.setItem(
      OVERRIDE_SESSION_STORAGE_KEY,
      JSON.stringify(overridesFromSessionStorage),
    );
    window.history.pushState({}, '', window.location.pathname);
    window.location.reload();
  };

  /**
   * Gets resolved overrides
   */
  const overrides = useMemo(() => {
    if (!isRemoteOverridesEnabled) {
      return {};
    }

    const resolvedOverrides = Object.fromEntries(
      Object.entries(getRemoteOverrides()).filter(([scope]) =>
        Boolean(getRemoteRuntime(scope)),
      ),
    );

    return resolvedOverrides;
  }, [isRemoteOverridesEnabled]);

  /**
   * Determines if overlay should be displayed
   */
  const shouldDisplayOverrideOverlay = useMemo(() => {
    if (shouldShowReactScanInDevBanner()) {
      return true;
    }

    if (!isRemoteOverridesEnabled) {
      return false;
    }

    return preview || Object.values(overrides).length > 0;
  }, [isRemoteOverridesEnabled, preview, overrides]);

  const value = useMemo(() => ({ overrides }), [overrides]);

  return (
    <RemoteOverridesContext.Provider value={value}>
      {shouldDisplayOverrideOverlay && (
        <OverrideOverlay
          preview={preview}
          onPreviewResetClick={onPreviewResetClick}
          onRemoteOverrideResetClick={onRemoteOverrideResetClick}
        />
      )}
      {children}
    </RemoteOverridesContext.Provider>
  );
};

RemoteOverridesProvider.propTypes = {
  children: PropTypes.node,
};

export default RemoteOverridesProvider;
